// src/pages/PropertyContacts/components/PropertyContactFormModal.tsx
import React, { useState, useEffect } from "react";
import { X, Phone } from "lucide-react";
import { FormModal, FormField } from "../../../components/common/Forms";
import {
  useCreatePropertyContactMutation,
  useUpdatePropertyContactMutation,
} from "../../../store/api/propertyContactApi";
import type {
  PropertyContactResponseDto,
  CreatePropertyContactDto,
  UpdatePropertyContactDto,
} from "../../../types/property-contact";

interface PropertyContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title: string;
  contact?: PropertyContactResponseDto | null;
}

interface FormData extends Record<string, unknown> {
  phone: string;
}

interface FormErrors extends Record<string, string | undefined> {
  phone?: string;
}

const PropertyContactFormModal: React.FC<PropertyContactFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  title,
  contact,
}) => {
  const [formData, setFormData] = useState<FormData>({
    phone: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const [createPropertyContact, { isLoading: isCreating }] =
    useCreatePropertyContactMutation();
  const [updatePropertyContact, { isLoading: isUpdating }] =
    useUpdatePropertyContactMutation();

  const isEditing = !!contact;
  const isLoading = isCreating || isUpdating;

  // Reset form when modal opens/closes or contact changes
  useEffect(() => {
    if (isOpen) {
      if (contact) {
        setFormData({
          phone: contact.phone,
        });
      } else {
        setFormData({
          phone: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, contact]);


  const handleSubmit = async (values: Record<string, unknown>) => {
    const phone = values.phone as string;
    
    if (!phone?.trim()) {
      setErrors({ phone: "Phone number is required" });
      return;
    }
    
    if (!/^[\+]?[\d\s\-\(\)]{8,20}$/.test(phone.trim())) {
      setErrors({ phone: "Invalid phone number format" });
      return;
    }

    try {
      if (isEditing && contact) {
        const updateData: UpdatePropertyContactDto = {
          phone: phone.trim(),
        };
        await updatePropertyContact({
          id: contact.id,
          data: updateData,
        }).unwrap();
      } else {
        const createData: CreatePropertyContactDto = {
          phone: phone.trim(),
        };
        await createPropertyContact(createData).unwrap();
      }

      onSuccess();
    } catch (error: any) {
      // Handle validation errors from API
      if (error?.data?.errors) {
        const apiErrors: FormErrors = {};
        error.data.errors.forEach((err: any) => {
          if (err.field && err.message) {
            apiErrors[err.field as keyof FormErrors] = err.message;
          }
        });
        setErrors(apiErrors);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={handleSubmit}
      loading={isLoading}
      submitText={isEditing ? "Update Contact" : "Create Contact"}
      initialValues={formData}
    >
      <div className="space-y-6">
        {/* Phone Number Field */}
        <FormField
          type="tel"
          name="phone"
          label="Phone Number"
          required
          {...(errors.phone && { error: errors.phone })}
          leftIcon={<Phone className="h-4 w-4" />}
          placeholder="+959123456789"
          value={formData.phone}
          onChange={(value) => handleInputChange("phone", value as string)}
          disabled={isLoading}
        />

        {/* Help Text */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <h4 className="text-sm font-medium text-blue-800 mb-2">
            Phone Number Format
          </h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Include country code (e.g., +959123456789)</li>
            <li>• Must be between 8-20 characters</li>
            <li>• Can include spaces, dashes, and parentheses</li>
            <li>• Examples: +959123456789, 09-123-456-789</li>
          </ul>
        </div>
      </div>
    </FormModal>
  );
};

export default PropertyContactFormModal;